import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { FaPlus, FaCalculator, FaInfoCircle, FaHome } from "react-icons/fa";
import { Link } from "react-router-dom";

const AverageDayCalculator = () => {
  const [dates, setDates] = useState([
    { id: 1, value: "" },
    { id: 2, value: "" },
  ]);
  const [average, setAverage] = useState(null);
  const [showInfo, setShowInfo] = useState(false);

  const handleDateChange = (id, value) => {
    setDates(dates.map((date) => (date.id === id ? { ...date, value } : date)));
  };

  const addDateField = () => {
    setDates([...dates, { id: dates.length + 1, value: "" }]);
  };

  const removeDateField = (id) => {
    if (dates.length > 2) {
      setDates(dates.filter((date) => date.id !== id));
    }
  };

  const calculateAverage = () => {
    const validDates = dates
      .filter((date) => date.value)
      .map((date) => new Date(date.value))
      .sort((a, b) => a - b);

    if (validDates.length < 2) {
      alert("Please enter at least 2 period dates");
      return;
    }

    const differences = [];
    for (let i = 1; i < validDates.length; i++) {
      const diff = (validDates[i] - validDates[i - 1]) / (1000 * 60 * 60 * 24);
      differences.push(diff);
    }

    const sum = differences.reduce((acc, curr) => acc + curr, 0);
    const avg = sum / differences.length;
    setAverage(avg.toFixed(2));
  };

  const resetCalculator = () => {
    setDates([
      { id: 1, value: "" },
      { id: 2, value: "" },
    ]);
    setAverage(null);
  };

  return (
    <div className="w-full min-h-screen font-nunito bg-gradient-to-br from-pink-50 to-purple-50 py-8">
   <Helmet>
    {/* Google Analytics */}
    <script
      async
      src="https://www.googletagmanager.com/gtag/js?id=G-YR8YMW9ERY"
    ></script>
    <script>
      {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-YR8YMW9ERY', {
          page_path: window.location.pathname,
        });
      `}
    </script>
    <title>Average Menstrual Cycle Calculator - FlowHearts | Track Period Length</title>
    <meta
      name="description"
      content="Calculate your average menstrual cycle length with FlowHearts' Period Calculator. Input period start dates to track cycle patterns and boost women's health."
    />

    {/* Canonical URL */}
    <link rel="canonical" href="https://flowhearts.com/average-day-calculator" />

    {/* Open Graph / Facebook */}
    <meta property="og:type" content="website" />
    <meta property="og:url" content="https://flowhearts.com/average-day-calculator" />
    <meta
      property="og:title"
      content="Average Menstrual Cycle Calculator - FlowHearts | Track Period Length"
    />
    <meta
      property="og:description"
      content="Use FlowHearts' Period Calculator to find your average menstrual cycle length. Track period start dates for insights into cycle patterns and women's health."
    />
    <meta
      property="og:image"
      content="https://flowhearts.com/images/average-day-calculator-preview.jpg"
    />

    {/* Twitter */}
    <meta property="twitter:card" content="summary_large_image" />
    <meta property="twitter:url" content="https://flowhearts.com/average-day-calculator" />
    <meta
      property="twitter:title"
      content="Average Menstrual Cycle Calculator - FlowHearts | Track Period Length"
    />
    <meta
      property="twitter:description"
      content="Track your menstrual cycle length with FlowHearts' Period Calculator. Enter period start dates to monitor cycle patterns and improve health awareness."
    />
    <meta
      property="twitter:image"
      content="https://flowhearts.com/images/average-day-calculator-preview.jpg"
    />

    {/* Structured Data */}
    <script type="application/ld+json">
      {`
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "FlowHearts Average Menstrual Cycle Calculator",
          "url": "https://flowhearts.com/average-day-calculator",
          "description": "Calculate your average menstrual cycle length by entering period start dates with FlowHearts' Period Calculator for better health tracking.",
          "applicationCategory": "HealthApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "creator": {
            "@type": "Organization",
            "name": "FlowHearts"
          }
        }
      `}
    </script>
  </Helmet>
      <div className="max-w-8xl mx-auto px-4 md:px-6">
        {/* Back to Home Button */}
        <div className="flex justify-start mb-6">
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md hover:bg-pink-50 transition-colors duration-200"
          >
            <FaHome className="text-pink-500" />
            <span className="text-purple-700 font-medium">Back to Home</span>
          </Link>
        </div>

        <div className="bg-white rounded-xl shadow-md p-4 md:p-6">
          <header className="text-center mb-6">
            <h2 className="text-4xl font-bold font-cormorant text-purple-800 mb-2 flex items-center justify-center">
              <FaCalculator className="mr-2" />
              Average Cycle Length Calculator
            </h2>
            <p className="text-pink-600">
              Calculate your average menstrual cycle length
            </p>
          </header>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-purple-700">
                Enter Period Start Dates
              </h2>
              <button
                onClick={() => setShowInfo(!showInfo)}
                className="text-purple-600 hover:text-purple-800"
              >
                <FaInfoCircle size={18} />
              </button>
            </div>

            {showInfo && (
              <div className="bg-purple-50 p-4 rounded-lg mb-4 border border-purple-100">
                <h3 className="font-semibold text-purple-800 mb-2">
                  How to Calculate:
                </h3>
                <ol className="list-decimal pl-5 space-y-1 text-sm">
                  <li>List start dates of at least 2-5 periods</li>
                  <li>Calculator finds days between each cycle</li>
                  <li>Averages all the cycle lengths</li>
                  <li>More dates = more accurate results</li>
                </ol>
              </div>
            )}

            <div className="space-y-3">
              {dates.map((date, index) => (
                <div key={date.id} className="flex items-center">
                  <label className="w-24 text-sm font-medium text-gray-700">
                    Period {index + 1}:
                  </label>
                  <input
                    type="date"
                    value={date.value}
                    onChange={(e) => handleDateChange(date.id, e.target.value)}
                    className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
                  />
                  {dates.length > 2 && (
                    <button
                      onClick={() => removeDateField(date.id)}
                      className="ml-2 text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={addDateField}
              className="mt-3 flex items-center text-sm text-purple-600 hover:text-purple-800"
            >
              <FaPlus className="mr-1" />
              Add another period date
            </button>
          </div>

          <div className="flex flex-wrap gap-3 justify-center mb-6">
            <button
              onClick={calculateAverage}
              className="px-6 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all"
            >
              Calculate Average
            </button>
            <button
              onClick={resetCalculator}
              className="px-6 py-2 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all"
            >
              Reset
            </button>
          </div>

          {average && (
            <div className="bg-gradient-to-r from-pink-100 to-purple-100 p-6 rounded-xl text-center">
              <h3 className="text-lg font-semibold text-purple-800 mb-2">
                Your Average Cycle Length
              </h3>
              <p className="text-4xl font-bold text-pink-600">{average} days</p>

              <div className="mt-4 bg-white bg-opacity-50 p-3 rounded-lg">
                <h4 className="font-medium text-purple-800 mb-1">
                  Interpretation:
                </h4>
                <p className="text-sm">
                  {average < 21 &&
                    "Your cycles are shorter than typical (21-35 days). Consider consulting a healthcare provider."}
                  {average >= 21 &&
                    average <= 35 &&
                    "Your cycle length is within the typical range (21-35 days)."}
                  {average > 35 &&
                    "Your cycles are longer than typical (21-35 days). This may indicate irregular cycles."}
                </p>
              </div>
            </div>
          )}

          <div className="mt-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="font-semibold text-purple-800 mb-2">
              📘 Tips for Best Results
            </h3>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li>Use at least 3-5 cycles for better accuracy</li>
              <li>Mark the first day of bleeding (not spotting)</li>
              <li>
                Track consistently for several months to identify patterns
              </li>
              <li>Irregular cycles may require more data points</li>
            </ul>
          </div>
        </div>
      </div>
      {/* Educational Section */}
      <div className="max-w-8xl mx-auto px-4 md:px-6 mt-12">
        <div className="bg-white rounded-xl shadow-md p-6 md:p-8">
          {/* How to Use Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaInfoCircle className="mr-3 text-pink-500" />
              How to Use This Calculator
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  1
                </div>
                <h3 className="text-lg font-semibold text-center text-pink-700 mb-2">
                  Enter Dates
                </h3>
                <p className="text-gray-700 text-center">
                  Input the first day of at least 2 menstrual periods. More
                  dates increase accuracy.
                </p>
              </div>

              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  2
                </div>
                <h3 className="text-lg font-semibold text-center text-purple-700 mb-2">
                  Calculate
                </h3>
                <p className="text-gray-700 text-center">
                  The calculator finds days between each cycle and averages
                  them.
                </p>
              </div>

              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  3
                </div>
                <h3 className="text-lg font-semibold text-center text-pink-700 mb-2">
                  Interpret
                </h3>
                <p className="text-gray-700 text-center">
                  Typical cycles are 21-35 days. Results outside this range may
                  need medical attention.
                </p>
              </div>
            </div>
          </div>

          {/* The Science Behind */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaCalculator className="mr-3 text-purple-500" />
              The Science Behind the Calculation
            </h2>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200 mb-6">
              <h3 className="text-xl font-semibold text-purple-800 mb-4">
                Core Formula
              </h3>
              <div className="bg-white p-4 rounded-lg shadow-sm mb-3">
                <code className="block font-mono text-purple-700">
                  Average = (Σ(Difference between consecutive cycles)) / (Number
                  of cycles - 1)
                </code>
              </div>
              <p className="text-gray-700">
                For example, with cycles starting on Day 1, Day 28, and Day 56:
              </p>
              <div className="bg-white p-3 rounded-lg mt-2">
                <code className="block font-mono text-sm text-purple-700">
                  ( (28-1) + (56-28) ) / 2 = (27 + 28)/2 = 27.5 days average
                </code>
              </div>
              <p className="text-gray-700 mt-4">
                This method provides a reliable way to understand your menstrual cycle patterns, helping in better women's health management. Many people use similar calculations in period tracker apps to predict future cycles and monitor reproductive health.
              </p>
            </div>
          </div>

          {/* Accuracy Note */}
          <div className="bg-white rounded-xl p-6 shadow-inner border border-pink-300 mb-12">
            <h3 className="text-xl font-semibold text-pink-800 mb-4 flex items-center">
              <FaInfoCircle className="mr-2 text-pink-500" />
              Important Note on Accuracy
            </h3>
            <p className="text-gray-700 mb-3">
              While this calculator provides useful estimates, menstrual cycles
              can vary due to:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <FaInfoCircle className="text-pink-500 mr-2 mt-1 flex-shrink-0" />
                <span>Stress (can alter cycles by 2-7 days)</span>
              </li>
              <li className="flex items-start">
                <FaInfoCircle className="text-pink-500 mr-2 mt-1 flex-shrink-0" />
                <span>Hormonal changes (±1-14 days variation)</span>
              </li>
              <li className="flex items-start">
                <FaInfoCircle className="text-pink-500 mr-2 mt-1 flex-shrink-0" />
                <span>Health conditions (PCOS, thyroid issues, etc.)</span>
              </li>
            </ul>
            <p className="text-gray-700 mt-4">
              For best results, track 6-12 months of data and consult a
              healthcare provider if your cycles are consistently irregular. Consistent cycle tracking can reveal insights into overall wellness and help in early detection of potential issues.
            </p>
          </div>

          {/* New Section: Why Track Your Menstrual Cycle? */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaInfoCircle className="mr-3 text-pink-500" />
              Why Track Your Menstrual Cycle?
            </h2>
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-200">
              <p className="text-gray-700 mb-4">
                Understanding your average cycle length is more than just numbers—it's a key part of empowering your health journey. In today's fast-paced world, many women are turning to cycle tracking to gain better control over their bodies.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <FaInfoCircle className="text-purple-500 mr-2 mt-1 flex-shrink-0" />
                  <span><strong>Fertility Awareness:</strong> Knowing your cycle helps in planning or avoiding pregnancy naturally, aligning with trends in natural family planning methods.</span>
                </li>
                <li className="flex items-start">
                  <FaInfoCircle className="text-purple-500 mr-2 mt-1 flex-shrink-0" />
                  <span><strong>Health Monitoring:</strong> Spot irregularities early, which could indicate conditions like endometriosis or hormonal imbalances, a growing focus in women's health discussions.</span>
                </li>
                <li className="flex items-start">
                  <FaInfoCircle className="text-purple-500 mr-2 mt-1 flex-shrink-0" />
                  <span><strong>Mood and Energy Insights:</strong> Track how your cycle affects daily life, helping optimize workouts, diet, and self-care routines for better overall well-being.</span>
                </li>
                <li className="flex items-start">
                  <FaInfoCircle className="text-purple-500 mr-2 mt-1 flex-shrink-0" />
                  <span><strong>Empowerment:</strong> Join the movement towards body positivity and informed health choices by staying in tune with your natural rhythms.</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4">
                With rising interest in menstrual health apps and tools, starting with a simple average cycle calculator like this can be your first step towards a more informed lifestyle.
              </p>
            </div>
          </div>

          {/* New Section: Frequently Asked Questions */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaInfoCircle className="mr-3 text-purple-500" />
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-700 mb-2">
                  What is a normal menstrual cycle length?
                </h3>
                <p className="text-gray-700">
                  A typical menstrual cycle ranges from 21 to 35 days, but individual variations are common. Tracking your own patterns helps determine what's normal for you.
                </p>
              </div>
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-2">
                  How many cycles should I track for accurate results?
                </h3>
                <p className="text-gray-700">
                  Aim for at least 3-6 cycles. This provides a better average and accounts for monthly fluctuations influenced by lifestyle factors.
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-700 mb-2">
                  Can stress affect my cycle length?
                </h3>
                <p className="text-gray-700">
                  Yes, stress can shorten or lengthen cycles. Incorporating mindfulness or exercise can help stabilize patterns, as highlighted in recent women's health trends.
                </p>
              </div>
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-2">
                  When should I see a doctor about my cycles?
                </h3>
                <p className="text-gray-700">
                  If your average exceeds 35 days, is under 21 days, or changes suddenly, consult a healthcare professional. Early attention to irregular periods can prevent complications.
                </p>
              </div>
            </div>
          </div>

          {/* New Section: Benefits of Knowing Your Average Cycle */}
          <div>
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaCalculator className="mr-3 text-pink-500" />
              Benefits of Knowing Your Average Cycle
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-2">
                  Better Planning
                </h3>
                <p className="text-gray-700">
                  Predict upcoming periods to prepare for travel, events, or sports activities, reducing surprises in your daily routine.
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-700 mb-2">
                  Holistic Health
                </h3>
                <p className="text-gray-700">
                  Connect cycle data with nutrition and fitness for personalized wellness strategies, a popular approach in modern health communities.
                </p>
              </div>
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-2">
                  Fertility Optimization
                </h3>
                <p className="text-gray-700">
                  Identify fertile windows for those trying to conceive, supporting natural fertility methods without advanced tech.
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-700 mb-2">
                  Emotional Well-being
                </h3>
                <p className="text-gray-700">
                  Understand how hormonal shifts influence mood, allowing for proactive self-care during different phases of your cycle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AverageDayCalculator;